import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, api } from '../../../shared/api'
import type { Schultyp, StatusFilter } from '../schultyp'

/** Contract §4.4: IDs remain strings. A row is a school WITHOUT a valid type, active or inactive (owner t10b_inaktiv = b). */
export interface SchultypOhneSchule {
  schoolId: string
  schulName: string
  schulnummer: string | null
  stadt: string | null
  /** The raw value today: null, or an old name such as 'Hort' until migration 383 has run. */
  aktuellerWert: string | null
  /** false = inactive school: the list marks it "inaktiv". Migration 383 stops on it like on an active one. */
  aktiv: boolean
}

export interface SchultypOhneListe {
  zeilen: SchultypOhneSchule[]
  gesamt: number
  seite: number
  proSeite: number
}

export interface SchultypOhneFilter {
  seite?: number
  proSeite?: number
  q?: string
  /** `alle` (default) lists active and inactive schools; `aktiv` / `inaktiv` filter on the server. */
  status?: StatusFilter
}

export function useSchultypOhne({ seite = 1, proSeite = 25, q = '', status = 'alle' }: SchultypOhneFilter = {}, enabled = true) {
  const suche = q.trim()
  return useQuery({
    queryKey: ['schulen-schultyp', seite, proSeite, suche, status],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ seite: String(seite), proSeite: String(proSeite), q: suche, status })
      return apiFetch<SchultypOhneListe>(`/api/schulen-schultyp?${params}`, { signal })
    },
    enabled,
    staleTime: 0,
  })
}

export function useSetSchultyp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ schoolId, schultyp }: { schoolId: string; schultyp: Schultyp }) =>
      api.put<{ schoolId: string; schulName: string; schultyp: Schultyp; geaendert: boolean }>(
        `/api/schulen-schultyp/${encodeURIComponent(schoolId)}`, { schultyp }),
    onSuccess: async () => {
      // The school leaves the list, and every school list that shows the type (dashboard, editor) must read it again.
      await qc.invalidateQueries({ predicate: ({ queryKey }) => queryKey[0] === 'schulen-schultyp' || queryKey[0] === 'schulen' })
    },
  })
}
